import type { ReactNode } from 'react';

export interface ZoomControlsProps {
  /** Current zoom level as a fraction (1 = 100%) */
  zoom: number;
  /** Called when the user changes the zoom level */
  onZoomChange: (zoom: number) => void;
  /** The auto-calculated fit-to-width zoom. Selecting "Fit" uses this value. */
  fitZoom?: number;
  /**
   * Called instead of onZoomChange when the user picks "Fit", so the parent can
   * keep following the fit as the container resizes (see utils/fit-zoom.ts).
   */
  onFitSelect?: () => void;
  /** Whether the parent is in fit mode. When given, the dropdown shows "Fit" from this. */
  fitMode?: boolean;
  /** Optional undo/redo/extract action buttons shown on either side of the zoom widget. */
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onExtractClick?: () => void;
  /**
   * Delete the selected annotation(s). Omit to hide the control.
   *
   * Deleting was bound to Delete/Backspace alone until 2026-09-07, so on a
   * phone — which has no way to press either, the soft keyboard only opening
   * for a text field — a mark could be selected and never removed.
   */
  onDeleteSelected?: () => void;
  /** Whether anything is currently selected. */
  canDelete?: boolean;
  /**
   * Download the document with annotations burned in. Omit to hide.
   * Wording stays format-neutral: PDFs and PPTX come back as PDF, images as PNG.
   */
  onExportClick?: () => void;
  isExporting?: boolean;
  /** Open this document on the meeting stage for everyone. Omit to hide. */
  onShareToStage?: () => void;
  /**
   * Close the open document.
   *
   * A duplicate of the header Close, deliberately. Teams mobile draws its own
   * chrome over the top of the app frame — a Notifications bar that could not
   * be collapsed on 2026-08-27 — which hid our entire header row. Close lived
   * only there, so the user was left with a document they could not close and
   * no way back to the upload prompt, since that only renders when no
   * document is open. An exit must not sit exclusively in the one row the
   * host can cover.
   */
  onCloseDocument?: () => void;
  /** What the close control says; "Close document" unless the stage shows the whiteboard. */
  closeLabel?: string;
  isSharingToStage?: boolean;
  /**
   * A control for opening another document, rendered next to the other
   * actions. Passed in rather than built here because uploading owns real
   * state (auth, progress, the upgrade gate) that belongs in DocumentUpload.
   *
   * It lives in this row for the same reason Close does: on Teams mobile the
   * header can be covered by host chrome, and this is the row that survives.
   */
  openDocumentSlot?: ReactNode;
  /**
   * Return to the presenter's view. Omit unless follow mode is suspended.
   *
   * A duplicate of the Rejoin in MeetingControls, for the reason Close is
   * duplicated here: that row sits under Teams' uncollapsible Notifications
   * bar on Android. Suspension whose only exit is invisible is a trap, and
   * shipping one already cost a release (ff654b3).
   */
  onRejoinPresenter?: () => void;
}

const ZOOM_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;

function IconUndo(): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="4 7 1 4 4 1" />
      <path d="M1 4h9a4 4 0 110 8H6" />
    </svg>
  );
}

function IconRedo(): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="12 7 15 4 12 1" />
      <path d="M15 4H6a4 4 0 100 8h4" />
    </svg>
  );
}

function IconDownload(): ReactNode {
  // Page with an arrow dropping out of it — "save this document", distinct
  // from the sparkle, which means "AI".
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 1v8" />
      <polyline points="5 6.5 8 9.5 11 6.5" />
      <path d="M2 11v2.5a1 1 0 001 1h10a1 1 0 001-1V11" />
    </svg>
  );
}

function IconExtract(): ReactNode {
  // Four-pointed sparkle — universal AI / magic cue. Big primary star plus
  // a small accent star, both filled to read clearly at 14px.
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1.5l1 3.5 3.5 1-3.5 1-1 3.5-1-3.5-3.5-1 3.5-1 1-3.5z" />
      <path d="M3.5 9l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6.6-1.9z" />
    </svg>
  );
}

function IconRejoin(): ReactNode {
  // An eye — the same cue MeetingControls uses for following the presenter.
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  );
}

function IconStage(): ReactNode {
  // A screen with the document on it — "put this up where everyone sees it".
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="1.5" y="2.5" width="13" height="9" rx="1" />
      <path d="M6 14h4" />
      <path d="M5.5 5.5h5M5.5 8h3" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

export function ZoomControls({
  zoom,
  onZoomChange,
  fitZoom,
  onFitSelect,
  fitMode,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onExtractClick,
  onDeleteSelected,
  canDelete,
  onExportClick,
  isExporting,
  onShareToStage,
  onCloseDocument,
  closeLabel = 'Close document',
  isSharingToStage,
  openDocumentSlot,
  onRejoinPresenter,
}: ZoomControlsProps) {
  const handleZoomIn = () => {
    const next = Math.min(ZOOM_MAX, Math.round((zoom + ZOOM_STEP) * 100) / 100);
    onZoomChange(next);
  };

  const handleZoomOut = () => {
    const next = Math.max(ZOOM_MIN, Math.round((zoom - ZOOM_STEP) * 100) / 100);
    onZoomChange(next);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'fit' && fitZoom) {
      if (onFitSelect) onFitSelect();
      else onZoomChange(fitZoom);
    } else {
      onZoomChange(parseFloat(val));
    }
  };

  // Find closest preset for the dropdown display
  const displayPercent = Math.round(zoom * 100);
  const isPreset = ZOOM_PRESETS.some((p) => Math.round(p * 100) === displayPercent);
  const isFit = fitZoom != null && (fitMode ?? Math.abs(zoom - fitZoom) < 0.005);

  const showActions =
    onUndo != null ||
    onRedo != null ||
    onExtractClick != null ||
    onExportClick != null ||
    onShareToStage != null;

  return (
    <div className="zoom-controls">
      <div className="zoom-controls__group zoom-controls__group--left">
        {openDocumentSlot}
        {showActions && onUndo && (
          <button
            className="zoom-controls__action-btn"
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo"
            aria-label="Undo"
          >
            <IconUndo />
          </button>
        )}
        {showActions && onRedo && (
          <button
            className="zoom-controls__action-btn"
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo"
            aria-label="Redo"
          >
            <IconRedo />
          </button>
        )}
        {showActions && onDeleteSelected && (
          <button
            className="zoom-controls__action-btn"
            onClick={onDeleteSelected}
            disabled={!canDelete}
            title="Delete the selected annotation"
            aria-label="Delete the selected annotation"
          >
            <IconTrash />
          </button>
        )}
        {showActions && onExportClick && (
          <button
            className="zoom-controls__action-btn"
            onClick={onExportClick}
            disabled={isExporting}
            title="Download a copy with the annotations included"
            aria-label="Download annotated copy"
          >
            <IconDownload />
          </button>
        )}
      </div>

      <div className="zoom-controls__group zoom-controls__group--center">
        <button
          className="zoom-controls__btn"
          onClick={handleZoomOut}
          disabled={zoom <= ZOOM_MIN}
          title="Zoom out"
        >
          &minus;
        </button>
        <select
          className="zoom-controls__select"
          value={isFit ? 'fit' : isPreset ? String(zoom) : ''}
          onChange={handleSelectChange}
        >
          {fitZoom && <option value="fit">Fit</option>}
          {ZOOM_PRESETS.map((p) => (
            <option key={p} value={String(p)}>
              {Math.round(p * 100)}%
            </option>
          ))}
          {!isPreset && !isFit && <option value={String(zoom)}>{displayPercent}%</option>}
        </select>
        <button
          className="zoom-controls__btn"
          onClick={handleZoomIn}
          disabled={zoom >= ZOOM_MAX}
          title="Zoom in"
        >
          +
        </button>
      </div>

      <div className="zoom-controls__group zoom-controls__group--right">
        {onRejoinPresenter && (
          <button
            className="zoom-controls__action-btn zoom-controls__action-btn--rejoin"
            onClick={onRejoinPresenter}
            title="Go back to the presenter's document"
            aria-label="Rejoin the presenter"
          >
            <IconRejoin />
            <span className="zoom-controls__action-btn-label">Rejoin</span>
          </button>
        )}
        {showActions && onShareToStage && (
          <button
            className="zoom-controls__action-btn zoom-controls__action-btn--share"
            onClick={onShareToStage}
            disabled={isSharingToStage}
            title="Open this document on the meeting stage for everyone"
            aria-label="Share to meeting stage"
          >
            <IconStage />
            <span className="zoom-controls__action-btn-label">Share</span>
          </button>
        )}
        {showActions && onExtractClick && (
          <button
            className="zoom-controls__action-btn zoom-controls__action-btn--extract"
            onClick={onExtractClick}
            title="Extract data with AI — Markdown, JSON, CSV, or text"
            aria-label="Extract data with AI"
          >
            <IconExtract />
            <span className="zoom-controls__action-btn-label">Extract</span>
          </button>
        )}
        {onCloseDocument && (
          <button
            className="zoom-controls__action-btn zoom-controls__action-btn--close"
            onClick={onCloseDocument}
            title={closeLabel}
            aria-label={closeLabel}
          >
            &times;
          </button>
        )}
      </div>
    </div>
  );
}
